import React, {
    useCallback,
    useMemo,
    useRef,
} from 'react';
import _get from 'lodash/get';

import {
    useReelStore,
    useModalStore,
    useClip,
    useUserData,
} from 'bdvp-common/src/hooks/store';
import {
    useMediaConfig,
    useDictionary,
    useLang,
} from 'bdvp-common/src/hooks/config';

import { constants as modalConstants } from 'bdvp-graphql/src/resolvers/modals';
import { Tracking } from 'bdvp-tracking';

import ReelsComponent from '../components/Reels';

const {
    REEL_MODAL_NAME,
    REEL_CREATION_MODAL_NAME,
    TRIM_REEL_CLIP_MODAL_NAME,
} = modalConstants;

const Reels = () => {

    const lang = useLang();
    const config = useMediaConfig();
    const dictionary = useDictionary();
    const {
        data,
        removeReelClip,
        reorderReelClip,
        setTrimmingMediaId,
    } = useReelStore();
    const {
        isModalOpen,
        modalToggle,
        modalHide,
        modalShow,
    } = useModalStore();
    const isOpen = isModalOpen(REEL_MODAL_NAME);
    const trimmingOpenRef = useRef(false);
    const mediaSlugs = useMemo(() => data?.reel?.reelClips?.map(item => item.clipSlug) || [] , [data]);
    const {
        loading: clipsLoading,
        data: clipsData,
    } = useClip(
        mediaSlugs.concat().sort(), // Sort so we do not generate requests for reordered clips
        {
            ssr: false,
            skip: !isOpen || data?.reel?.reelClips?.length < 1,
        }
    );

    // Sort data baseed on reelClips in state
    const sortedClipData = useMemo(() => clipsData
        ? clipsData.mediaPlayback.concat().sort((a, b) => mediaSlugs.indexOf(a.slug) - mediaSlugs.indexOf(b.slug))
        : undefined
    , [
        clipsData,
        mediaSlugs,
    ]);

    const toggleReelCreation = useCallback(() => modalToggle(REEL_MODAL_NAME), [modalToggle]);
    const closeReelCreation = useCallback(() => {

        // This is needed to stop the reels cart popover container from closing on a trimming event
        if(!trimmingOpenRef.current) {

            modalHide(REEL_MODAL_NAME);

        }

    }, [
        modalHide,
        trimmingOpenRef,
    ]);
    const openReelCreationModal = useCallback(() => {

        modalHide(REEL_MODAL_NAME);
        modalShow(REEL_CREATION_MODAL_NAME);

    }, [
        modalShow,
        modalHide,
    ]);

    const openClipTrimming = useCallback((mediaId) => {

        setTrimmingMediaId(mediaId).then(() => modalShow(TRIM_REEL_CLIP_MODAL_NAME));

    }, [
        setTrimmingMediaId,
        modalShow,
    ]);

    const { data: userData = {} } = useUserData();
    const { isLoggedIn = false, userId = '' } = userData;

    return (
        <ReelsComponent
            config={config}
            dictionary={dictionary}
            reel={data?.reel}
            removeClip={removeReelClip}
            reorder={reorderReelClip}
            isOpen={isOpen}
            toggleReelCreation={toggleReelCreation}
            closeReelCreation={closeReelCreation}
            openReelCreationModal={openReelCreationModal}
            openClipTrimming={openClipTrimming}
            onTrimmingOpened={() => trimmingOpenRef.current = true}
            onTrimmingClosed={() => {

                Tracking.trackReelCreationCloseClick();
                trimmingOpenRef.current = false;

            }}
            clipsLoading={clipsLoading}
            clipsData={sortedClipData}
            lang={lang}
            isLoggedIn={isLoggedIn}
            userId={userId}
        />
    );

};

export default Reels;
